import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../../FaIcon';
import {DURABILITY_NOISE_ORDER, FACT_TYPE_MODIFIER, durabilityLabelKey, factTypeLabelKey}
    from '../../memory/factConstants';
import {docSourceLabelKey} from '../../memory/docConstants';
import {sourceDate} from '../../memory/factDates';

/**
 * «Archivado solo»: lo que el triage de durabilidad archivó como ruido sin pedirle nada a nadie.
 *
 * Es la tercera sección hermana debajo del mazo, y la razón de que las otras dos sean más chicas.
 * «Ruido probable» pedía un click por familia; medido sobre el golden, la mitad de lo que el triage
 * degrada en una transcripción es ruido seguro (logística de la reunión, charla, datos de otro
 * cliente) que nadie iba a rescatar. Desde que el backend archiva esas familias solo —nacen
 * rechazadas, sin persona, con `auto_archived: true`—, el usuario no las ve como cartas ni como
 * grupos que descartar: las ve acá, colapsadas, por si alguna sirve.
 *
 * No es una cola: no bloquea, no cuenta en el avance ni en el número grande. Cada fila tiene un
 * solo camino de vuelta, «Restaurar», que la manda a la cola PRINCIPAL (no a «Ruido probable»): si
 * una persona la rescató del archivo, es porque vale una decisión de verdad.
 *
 * Los rechazados por una persona NO aparecen acá: los archiva ella, y su vuelta atrás es el
 * Deshacer del mazo. Qué entra y en qué orden lo decide `archivedQueue` en el store.
 */
// Un id estable por familia para que la cabecera de cada grupo pueda apuntar a su panel.
const groupId = (category) => `iabr-arch-g-${String(category).toLowerCase()}`;

export function ArchivedNoiseSection({
    facts, failed, hidden, documentsById, isBusy, onRestore,
    // «Ver la cita» sobre el documento de origen: abre el visor único de la página, posicionado en
    // la cita. Sin él, el título del documento es texto plano, como hasta el 2026-09-12.
    onOpenSource,
}) {
    const {t} = useTranslation();
    // Colapsada por default y sin memoria entre visitas: es un archivo, no trabajo pendiente. Se
    // abre cuando alguien busca algo.
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState({});

    const rows = facts || [];
    // Si el listado del archivo no llegó, la sección lo dice: vacía y muda era indistinguible de
    // «no hay nada archivado» para quien busca un hecho que el triage se comió (C-memoria-27).
    if (rows.length === 0 && !failed) return null;

    const byCategory = {};
    rows.forEach((f) => {
        const cat = DURABILITY_NOISE_ORDER.includes(f.durability_class) ? f.durability_class : 'OTHER';
        (byCategory[cat] = byCategory[cat] || []).push(f);
    });
    const groups = [...DURABILITY_NOISE_ORDER, 'OTHER']
        .map(cat => ({cat, items: byCategory[cat] || []}))
        .filter(g => g.items.length > 0);
    const busy = (id) => (isBusy ? isBusy(id) : false);

    return (
        <section className="iabr-arch" aria-labelledby="iabr-arch-title">
            <button type="button" className="iabr-arch-head" aria-expanded={open}
                    aria-controls="iabr-arch-panel" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                <span className="iabr-arch-title" id="iabr-arch-title">
                    {t('ia_booster.archived.title')}
                </span>
                {/* Con la carga caída no se pinta el conteo: «0 hechos» es un número que nadie
                    sabe —el archivo puede tener cientos— y competía con el aviso de error de
                    abajo, que dice la verdad (QA 2026-09-06, caso 16). */}
                {!(failed && rows.length === 0) && (
                    <span className="iabr-arch-count">
                        {t('ia_booster.deck.fact_count', {count: rows.length})}
                    </span>
                )}
            </button>
            <p className="iabr-arch-lede">{t('ia_booster.archived.lede')}</p>
            {failed && (
                <p className="iabr-arch-failed" role="alert">{t('ia_booster.archived.load_failed')}</p>
            )}
            {/* Lo que la lista NO muestra tiene que poder decirse. Un archivo que se corta sin
                avisar es exactamente «descartar en silencio», sólo que del lado de la pantalla — y
                eso es lo que este frente entero vino a sacar. Deja de ser un caso teórico cuando el
                material interno de la agencia empieza a entrar: un solo documento puede llenar la
                ventana. */}
            {hidden > 0 && (
                <p className="iabr-arch-hidden">{t('ia_booster.archived.hidden', {count: hidden})}</p>
            )}

            {/* Los grupos van en un contenedor propio para que la cabecera tenga a qué
                apuntar con `aria-controls`. */}
            {open && <div id="iabr-arch-panel">{groups.map((group) => {
                const isOpen = !!expanded[group.cat];
                return (
                    <div key={group.cat} className="iabr-arch-group" id={groupId(group.cat)}>
                        <div className="iabr-arch-group-head">
                            <button type="button" className="iabr-arch-group-toggle"
                                    aria-expanded={isOpen}
                                    aria-controls={`${groupId(group.cat)}-list`}
                                    onClick={() => setExpanded(prev => ({
                                        ...prev, [group.cat]: !prev[group.cat],
                                    }))}>
                                <FaIcon iconName={isOpen ? 'chevron-down' : 'chevron-right'}/>
                                <span className="iabr-arch-group-title">
                                    {t(durabilityLabelKey(group.cat))}
                                </span>
                                <span className="iabr-arch-group-count">{group.items.length}</span>
                            </button>
                        </div>
                        {isOpen && (
                            <ul className="iabr-arch-list" id={`${groupId(group.cat)}-list`}>
                                {group.items.map((fact) => {
                                    const doc = fact.source_document_id
                                        ? (documentsById || {})[fact.source_document_id]
                                        : null;
                                    const accent = FACT_TYPE_MODIFIER[fact.fact_type] || '-other';
                                    const docMeta = doc
                                        ? [t(docSourceLabelKey(doc.source)), sourceDate(doc)]
                                            .filter(Boolean).join(' · ')
                                        : '';
                                    // El texto del hecho lo escribió la extracción: va como
                                    // children y en el `aria-label` como texto plano, nunca
                                    // interpolado en una clave (i18next lo escaparía).
                                    return (
                                        <li key={fact.id} className="iabr-arch-row">
                                            <div className="iabr-arch-body">
                                                <div className="iabr-arch-chips">
                                                    <span className={`iabr-chip -type ${accent}`}>
                                                        {t(factTypeLabelKey(fact.fact_type))}
                                                    </span>
                                                    {doc && (
                                                        <span className="iabr-arch-doc">
                                                            <FaIcon iconName="file-lines"/>
                                                            {/* La flecha va FUERA del `<span>` que
                                                                trunca, no adentro: `.iabr-arch-doc-n`
                                                                recorta con elipsis a 18em, y un
                                                                título largo se habría comido la
                                                                señal justo cuando más se necesita.
                                                                Es la misma flecha del archivo de
                                                                Memoria — el gesto es el mismo y no
                                                                puede decirse de dos maneras
                                                                (QA-VIS-cita-06). */}
                                                            {onOpenSource && doc.has_raw !== false ? (
                                                                <button type="button" className="iabr-arch-doc-link"
                                                                        aria-label={`${t('ia_booster.deck.source_quote_in')}: ${doc.title}`}
                                                                        onClick={() => onOpenSource(doc, fact)}>
                                                                    <span className="iabr-arch-doc-n">{doc.title}</span>
                                                                    <span className="iabr-arch-doc-arrow" aria-hidden="true">&rarr;</span>
                                                                </button>
                                                            ) : (
                                                                <span className="iabr-arch-doc-n">{doc.title}</span>
                                                            )}
                                                            {docMeta && (
                                                                <span className="iabr-arch-doc-m">{docMeta}</span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="iabr-arch-statement">{fact.statement}</p>
                                            </div>
                                            <div className="iabr-arch-acts">
                                                <button type="button" className="iabr-btn -ghost"
                                                        disabled={busy(fact.id)}
                                                        aria-label={`${t('ia_booster.archived.restore')}: ${fact.statement}`}
                                                        onClick={() => onRestore(fact)}>
                                                    {t('ia_booster.archived.restore')}
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                );
            })}</div>}
        </section>
    );
}

export default ArchivedNoiseSection;
